/**
 * Central chat store (zustand) — 06 §9 stores. Holds conversations, per-conv
 * message lists, presence map, typing map, drafts, reply state, selection, and
 * connection state. Subscribes to wsClient and folds inbound frames into state,
 * applying the contract's ordering rule: order by server `seq`, never `ts`.
 */

import type { ServerToClientFrame } from '@sinyal/contracts';
import { create } from 'zustand';
import { open } from '../data/codec';
import { ulid } from '../data/ids';
import {
  persistConversation,
  persistMessage,
  loadConversationMessages,
} from '../data/dexieDb';
import { ALL_USERS_BY_ID, CONVERSATIONS, MESSAGE_HISTORY, ME } from '../data/mockData';
import type {
  Conversation,
  Message,
  MessageStatus,
  PresenceInfo,
  User,
} from '../data/model';
import { wsClient, type ConnectionState } from '../data/wsClient';

interface TypingState {
  /** conversationId -> set of typing user ids */
  [cid: string]: string[];
}

interface ChatState {
  me: User;
  connection: ConnectionState;
  conversations: Conversation[];
  /** conversationId -> ordered messages (ascending seq). */
  messagesByConv: Record<string, Message[]>;
  presence: Record<string, PresenceInfo>;
  typing: TypingState;
  /** conversationId -> reply target. */
  replyTo: Record<string, Message | null>;
  /** Multi-select mode (06 §4.3). conversationId -> selected clientMsgIds. */
  selection: Record<string, string[]>;

  // lifecycle
  init: () => void;

  // selectors
  getConversation: (cid: string) => Conversation | undefined;
  getMessages: (cid: string) => Message[];
  peerOf: (cid: string) => User | undefined;
  userById: (id: string) => User | undefined;

  // actions
  sendText: (cid: string, text: string) => void;
  retryMessage: (cid: string, clientMsgId: string) => void;
  setDraft: (cid: string, draft: string) => void;
  setReplyTo: (cid: string, message: Message | null) => void;
  markRead: (cid: string) => void;
  togglePin: (cid: string) => void;
  toggleMute: (cid: string) => void;
  toggleArchive: (cid: string) => void;
  deleteForMe: (cid: string, clientMsgId: string) => void;
  deleteForEveryone: (cid: string, clientMsgId: string) => void;
  startSelection: (cid: string, clientMsgId: string) => void;
  toggleSelect: (cid: string, clientMsgId: string) => void;
  clearSelection: (cid: string) => void;
  notifyTyping: (cid: string) => void;
}

let typingDebounce: ReturnType<typeof setTimeout> | null = null;

/** Decrypt a stored-message ciphertext into a view-model Message. */
function decryptToMessage(args: {
  cid: string;
  senderId: string;
  clientMsgId: string;
  seq: number | null;
  ciphertext: string;
  serverTs: number;
  replyToSeq: number | null;
  status: MessageStatus;
}): Message {
  const body = open(args.ciphertext);
  return {
    clientMsgId: args.clientMsgId,
    conversationId: args.cid,
    senderId: args.senderId,
    seq: args.seq,
    contentType: body.content_type,
    text: body.text,
    media: body.media
      ? {
          mediaId: body.media.media_id,
          mime: body.media.mime,
          size: body.media.size,
          url: body.media.url,
          ...(body.media.duration_sec !== undefined
            ? { durationSec: body.media.duration_sec }
            : {}),
          ...(body.media.width !== undefined ? { width: body.media.width } : {}),
          ...(body.media.height !== undefined ? { height: body.media.height } : {}),
          ...(body.media.file_name !== undefined ? { fileName: body.media.file_name } : {}),
        }
      : null,
    replyToSeq: args.replyToSeq,
    serverTs: args.serverTs,
    sentAtMs: args.serverTs,
    status: args.status,
    deleted: false,
    edited: false,
    forwarded: body.forwarded ?? false,
  };
}

function sortBySeq(msgs: Message[]): Message[] {
  return [...msgs].sort((a, b) => {
    const sa = a.seq ?? Number.MAX_SAFE_INTEGER;
    const sb = b.seq ?? Number.MAX_SAFE_INTEGER;
    if (sa !== sb) return sa - sb;
    return a.sentAtMs - b.sentAtMs;
  });
}

export const useChatStore = create<ChatState>((set, get) => ({
  me: ME,
  connection: 'connecting',
  conversations: CONVERSATIONS.map((c) => ({ ...c })),
  messagesByConv: Object.fromEntries(
    Object.entries(MESSAGE_HISTORY).map(([cid, msgs]) => [cid, sortBySeq(msgs)]),
  ),
  presence: {},
  typing: {},
  replyTo: {},
  selection: {},

  init: () => {
    // Connection state stream.
    wsClient.onState((connection) => set({ connection }));
    // Frame stream.
    wsClient.onFrame((frame) => handleFrame(frame, set));
    wsClient.start();
    // Hydrate any persisted messages (drafts/sent across reloads).
    void (async () => {
      for (const conv of get().conversations) {
        const stored = await loadConversationMessages(conv.id);
        if (stored.length > 0) {
          set((s) => ({
            messagesByConv: {
              ...s.messagesByConv,
              [conv.id]: sortBySeq([...(s.messagesByConv[conv.id] ?? []), ...stored]).filter(
                dedupeByClientMsgId(),
              ),
            },
          }));
        }
      }
    })();
  },

  getConversation: (cid) => get().conversations.find((c) => c.id === cid),
  getMessages: (cid) => get().messagesByConv[cid] ?? [],
  peerOf: (cid) => {
    const conv = get().getConversation(cid);
    if (!conv || conv.type !== 'direct' || !conv.peerId) return undefined;
    return ALL_USERS_BY_ID.get(conv.peerId);
  },
  userById: (id) => ALL_USERS_BY_ID.get(id),

  sendText: (cid, rawText) => {
    const text = rawText.trim();
    if (!text) return;
    const clientMsgId = ulid();
    const replyTo = get().replyTo[cid] ?? null;
    const optimistic: Message = {
      clientMsgId,
      conversationId: cid,
      senderId: ME.id,
      seq: null,
      contentType: 'text',
      text,
      media: null,
      replyToSeq: replyTo?.seq ?? null,
      serverTs: Date.now(),
      sentAtMs: Date.now(),
      status: get().connection === 'online' ? 'pending' : 'pending',
      deleted: false,
      edited: false,
      forwarded: false,
    };
    appendMessage(set, cid, optimistic);
    void persistMessage(optimistic);

    wsClient.sendMessage({
      cid,
      clientMsgId,
      contentType: 'text',
      body: { text, content_type: 'text' },
      ...(replyTo?.seq != null ? { replyToSeq: replyTo.seq } : {}),
    });

    // Clear draft + reply.
    get().setDraft(cid, '');
    get().setReplyTo(cid, null);
  },

  retryMessage: (cid, clientMsgId) => {
    const msgs = get().messagesByConv[cid] ?? [];
    const m = msgs.find((x) => x.clientMsgId === clientMsgId);
    if (!m) return;
    patchMessage(set, cid, clientMsgId, { status: 'pending' });
    wsClient.sendMessage({
      cid,
      clientMsgId,
      contentType: m.contentType === 'system' ? 'text' : m.contentType,
      body: { text: m.text, content_type: m.contentType },
    });
  },

  setDraft: (cid, draft) => {
    set((s) => ({
      conversations: s.conversations.map((c) => (c.id === cid ? { ...c, draft } : c)),
    }));
    const conv = get().getConversation(cid);
    if (conv) void persistConversation({ ...conv, draft });
  },

  setReplyTo: (cid, message) =>
    set((s) => ({ replyTo: { ...s.replyTo, [cid]: message } })),

  markRead: (cid) => {
    const conv = get().getConversation(cid);
    if (!conv) return;
    const msgs = get().messagesByConv[cid] ?? [];
    const lastSeq = msgs.reduce((m, x) => Math.max(m, x.seq ?? 0), 0);
    if (lastSeq > 0) wsClient.sendRead(cid, lastSeq);
    if (conv.unreadCount !== 0) {
      set((s) => ({
        conversations: s.conversations.map((c) =>
          c.id === cid ? { ...c, unreadCount: 0 } : c,
        ),
      }));
    }
  },

  togglePin: (cid) =>
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === cid ? { ...c, pinned: !c.pinned } : c,
      ),
    })),

  toggleMute: (cid) =>
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === cid
          ? { ...c, mutedUntilMs: c.mutedUntilMs ? null : Date.now() + 8 * 3600_000 }
          : c,
      ),
    })),

  toggleArchive: (cid) =>
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === cid ? { ...c, archived: !c.archived } : c,
      ),
    })),

  deleteForMe: (cid, clientMsgId) => {
    set((s) => ({
      messagesByConv: {
        ...s.messagesByConv,
        [cid]: (s.messagesByConv[cid] ?? []).filter((m) => m.clientMsgId !== clientMsgId),
      },
    }));
  },

  deleteForEveryone: (cid, clientMsgId) => {
    patchMessage(set, cid, clientMsgId, {
      deleted: true,
      text: '',
      media: null,
    });
  },

  startSelection: (cid, clientMsgId) =>
    set((s) => ({ selection: { ...s.selection, [cid]: [clientMsgId] } })),

  toggleSelect: (cid, clientMsgId) =>
    set((s) => {
      const cur = s.selection[cid] ?? [];
      const next = cur.includes(clientMsgId)
        ? cur.filter((id) => id !== clientMsgId)
        : [...cur, clientMsgId];
      return { selection: { ...s.selection, [cid]: next } };
    }),

  clearSelection: (cid) =>
    set((s) => ({ selection: { ...s.selection, [cid]: [] } })),

  notifyTyping: (cid) => {
    wsClient.sendTyping(cid, true);
    if (typingDebounce) clearTimeout(typingDebounce);
    typingDebounce = setTimeout(() => wsClient.sendTyping(cid, false), 4000);
  },
}));

/* ----------------------------- frame folding ----------------------------- */

type SetFn = (
  partial:
    | Partial<ChatState>
    | ((s: ChatState) => Partial<ChatState>),
) => void;

function dedupeByClientMsgId(): (m: Message, i: number, arr: Message[]) => boolean {
  const seen = new Set<string>();
  return (m) => {
    if (seen.has(m.clientMsgId)) return false;
    seen.add(m.clientMsgId);
    return true;
  };
}

function appendMessage(set: SetFn, cid: string, m: Message): void {
  set((s) => {
    const cur = s.messagesByConv[cid] ?? [];
    const merged = sortBySeq([...cur.filter((x) => x.clientMsgId !== m.clientMsgId), m]);
    return {
      messagesByConv: { ...s.messagesByConv, [cid]: merged },
      conversations: bumpConversation(s.conversations, cid, m),
    };
  });
}

function patchMessage(
  set: SetFn,
  cid: string,
  clientMsgId: string,
  patch: Partial<Message>,
): void {
  set((s) => {
    const cur = s.messagesByConv[cid] ?? [];
    const next = cur.map((m) => (m.clientMsgId === clientMsgId ? { ...m, ...patch } : m));
    const updated = next.find((m) => m.clientMsgId === clientMsgId);
    if (updated) void persistMessage(updated);
    return { messagesByConv: { ...s.messagesByConv, [cid]: sortBySeq(next) } };
  });
}

/** Update the conversation row's lastMessage + updatedAt and re-sort. */
function bumpConversation(
  conversations: Conversation[],
  cid: string,
  m: Message,
): Conversation[] {
  return conversations.map((c) =>
    c.id === cid ? { ...c, lastMessage: m, updatedAtMs: m.sentAtMs } : c,
  );
}

/** Raise a receipt cursor across own messages up to `seq`. */
function applyReceipt(
  set: SetFn,
  cid: string,
  upToSeq: number,
  status: MessageStatus,
): void {
  set((s) => {
    const cur = s.messagesByConv[cid] ?? [];
    const rank: Record<MessageStatus, number> = {
      pending: 0,
      failed: 0,
      sent: 1,
      delivered: 2,
      read: 3,
    };
    const next = cur.map((m) => {
      if (m.senderId !== ME.id || m.seq == null || m.seq > upToSeq) return m;
      if (rank[status] <= rank[m.status]) return m;
      return { ...m, status };
    });
    return { messagesByConv: { ...s.messagesByConv, [cid]: next } };
  });
}

function handleFrame(frame: ServerToClientFrame, set: SetFn): void {
  switch (frame.t) {
    case 'msg.ack': {
      const { cid, client_msg_id, seq, server_ts } = frame;
      patchMessage(set, cid, client_msg_id, {
        seq,
        status: 'sent',
        serverTs: server_ts,
      });
      break;
    }
    case 'msg.new': {
      const { cid } = frame;
      const m = decryptToMessage({
        cid,
        senderId: frame.sender_id,
        clientMsgId: frame.client_msg_id,
        seq: frame.seq,
        ciphertext: frame.ciphertext,
        serverTs: frame.server_ts,
        replyToSeq: frame.reply_to_seq ?? null,
        status: 'read',
      });
      appendMessage(set, cid, m);
      void persistMessage(m);
      // Mark conversation unread if it isn't the actively-read one.
      set((s) => ({
        conversations: s.conversations.map((c) =>
          c.id === cid ? { ...c, unreadCount: c.unreadCount + 1 } : c,
        ),
        typing: { ...s.typing, [cid]: (s.typing[cid] ?? []).filter((u) => u !== frame.sender_id) },
      }));
      // Auto-acknowledge delivered.
      wsClient.sendDelivered(cid, frame.seq);
      break;
    }
    case 'msg.delivered':
      applyReceipt(set, frame.cid, frame.up_to_seq, 'delivered');
      break;
    case 'msg.read':
      applyReceipt(set, frame.cid, frame.up_to_seq, 'read');
      break;
    case 'typing.start':
      set((s) => {
        const cur = s.typing[frame.cid] ?? [];
        if (cur.includes(frame.user_id)) return {};
        return { typing: { ...s.typing, [frame.cid]: [...cur, frame.user_id] } };
      });
      break;
    case 'typing.stop':
      set((s) => ({
        typing: {
          ...s.typing,
          [frame.cid]: (s.typing[frame.cid] ?? []).filter((u) => u !== frame.user_id),
        },
      }));
      break;
    case 'presence.update':
      set((s) => ({
        presence: {
          ...s.presence,
          [frame.user_id]: {
            userId: frame.user_id,
            state: frame.state,
            lastSeenAtMs: frame.last_seen_at_ms ?? null,
          },
        },
      }));
      break;
    case 'conn.ready':
    case 'auth.ok':
    case 'sync.batch':
    case 'conv.updated':
    case 'conv.member_changed':
    case 'error':
    case 'pong':
      // Not surfaced in this demo build; handled silently.
      break;
    default:
      break;
  }
}
